import { isReportVisible } from './utils/reportVisibility';
import { OrganizeReportsModal } from './components/OrganizeReportsModal';
import { ReportLinkStatus } from './components/ReportLinkStatus';
import { isResourcePending } from './hooks/useAsyncResource';
import { reportHref } from './utils/starGridNavigation';
import AppLayout from '@amzn/awsui-components-core/app-layout';
import Button from '@amzn/awsui-components-core/button';
import ExpandableSection from '@amzn/awsui-components-core/expandable-section';
import Flashbar from '@amzn/awsui-components-core/flashbar';
import SideNavigation, { SideNavigationProps } from '@amzn/awsui-components-core/side-navigation';
import React, { useEffect } from 'react';
import { isSessionExpiredMessage } from './services/starGridHttp';
import { useListedReports } from './hooks/useListedReports';
import { Navigate } from 'react-router-dom';
import { IS_NON_PROD } from 'src/constants/EnvironmentConstants';
import { StarGridDataProvider, useStarGridData } from './context/StarGridDataContext';
import { useStarGridNav } from './hooks/useStarGridNav';
import { useStarGridNavGroups } from './hooks/useStarGridNavGroups';
import { StarGridView } from './starGridConstants';
import { useStarGridText } from './hooks/useStarGridText';
import { CreateReportPage } from './views/CreateReportPage';
import { InfraSetupView } from './views/InfraSetupView';
import { OnboardDatasetView } from './views/OnboardDatasetView';
import { ManageReportsView } from 'src/components/star-grid/views/ManageReportsView';
import { SnapshotManagerView } from 'src/components/star-grid/views/SnapshotManagerView';
import { ReportStudioView } from 'src/components/star-grid/views/ReportStudioView';
import { ReportView } from './views/ReportView';
import { StarGridHomeView } from './views/StarGridHomeView';
import styles from './components/styles/StarGridNav.module.scss';

const VIEW_BY_HREF: Record<string, Exclude<StarGridView, 'report'>> = {
  '#home': 'home',
  '#create-report': 'create-report',
  '#modify-report': 'modify-report',
  '#manage-reports': 'manage-reports',
  '#snapshot-manager': 'snapshot-manager',
  '#infra': 'infra',
  '#onboard-dataset': 'onboard-dataset'
};

// Views that manage their own full-width surface: they get the uncapped content width and the wider
// nav. Kept as one set because Create and Modify are the same editor -- when these were three
// separate inline lists, Modify was missing from two of them and rendered at the default capped
// width while Create filled the screen.
const WIDE_VIEWS: ReadonlySet<StarGridView> = new Set<StarGridView>(['create-report', 'modify-report', 'report']);

// Subset of the above that paints its own background edge to edge, so AppLayout's own content
// padding would only add a band of app background between the nav and the panel.
const FULL_BLEED_VIEWS: ReadonlySet<StarGridView> = new Set<StarGridView>(['create-report', 'modify-report']);

/**
 * Inner shell (inside the data provider) — owns nav state and renders the active sub-view. Split
 * from StarGridPage so the side navigation can read the shared PnL report list (for the grouped
 * Reports sections) via context.
 */
const StarGridShell: React.FC = () => {
  const text = useStarGridText();
  const { pnlReports } = useStarGridData();
  const listed = useListedReports(pnlReports.data);
  const { activeView, activeReportId, activeHref, setActiveView, openReport, navigationOpen, setNavigationOpen } = useStarGridNav(listed.reports);

  // Loaded once per visit: saves and deletes `reload` it, so moving between views need not re-ask.
  const { ensureLoaded: ensureReportsLoaded } = pnlReports;
  useEffect(() => {
    ensureReportsLoaded();
  }, [ensureReportsLoaded]);

  const reports = listed.reports.filter(isReportVisible);
  const onSaved = (reportId: number, reportName?: string) => {
    listed.keep(reportId, reportName ?? '');
    pnlReports.reload();
    openReport(reportId);
  };
  const nav = useStarGridNavGroups(reports, activeReportId);

  const content: React.ReactNode = (() => {
    switch (activeView) {
      case 'create-report':
        return (
          <CreateReportPage
            onCreated={onSaved}
            // Only the CREATE page offers "copy from an existing report": on the edit page the report
            // being edited IS the starting point, so importing another would silently replace it.
            importable={reports.map((report) => ({ id: report.pnl_report_id, label: report.report_name }))}
          />
        );
      case 'modify-report':
        // The SAME editor as create, seeded with a saved report instead of a template: one screen,
        // one notion of a report, so an edit cannot drift from how the report was built.
        return (
          <ReportStudioView
            onCreated={onSaved}
            source={{
              kind: 'report',
              // Every visible report: a Studio-saved spec reopens through its catalog template, and a
              // template-built one through its definition (see `loadStudioReport`).
              reports: reports.map((report) => ({ id: report.pnl_report_id, label: report.report_name }))
            }}
          />
        );
      case 'manage-reports':
        return <ManageReportsView />;
      case 'snapshot-manager':
        return <SnapshotManagerView />;
      case 'infra':
        return <InfraSetupView />;
      case 'onboard-dataset':
        return <OnboardDatasetView />;
      case 'report':
        return activeReportId !== undefined ? (
          <ReportView key={activeReportId} reportId={activeReportId} onBack={() => setActiveView('home')} />
        ) : (
          <ReportLinkStatus loading={isResourcePending(pnlReports)} hasError={Boolean(pnlReports.error)} onRetry={pnlReports.refresh} />
        );
      case 'home':
      default:
        return (
          <StarGridHomeView
            onCreate={() => setActiveView('create-report')}
            onModify={() => setActiveView('modify-report')}
            onOpenReport={openReport}
          />
        );
    }
  })();

  // Ordered by how often it is used: building and opening reports first, the reports themselves next,
  // and Admin -- shared config plus deactivating or deleting OTHER people's reports -- last and
  // collapsed, so a destructive screen is never the first thing under the cursor.
  const studioItems: SideNavigationProps.Item[] = [
    {
      type: 'section',
      text: text('studioTitle'),
      items: [
        { type: 'link', text: text('studioCreateNew'), href: '#create-report' },
        { type: 'link', text: text('studioEditReports'), href: '#modify-report' }
      ]
    }
  ];
  const adminItems: SideNavigationProps.Item[] = [
    {
      type: 'section',
      text: 'Admin',
      items: [
        { type: 'link', text: text('manageReportsTitle'), href: '#manage-reports' },
        { type: 'link', text: text('snapshotManagerTitle'), href: '#snapshot-manager' },
        { type: 'link', text: text('wizardTitle'), href: '#onboard-dataset' }
      ]
    }
  ];
  const onNavFollow = (event: CustomEvent<SideNavigationProps.FollowDetail>) => {
    event.preventDefault();
    const href = event.detail.href;
    const report = reports.find((candidate) => reportHref(candidate, reports) === href);
    if (report) {
      openReport(report.pnl_report_id);
      return;
    }
    const next = VIEW_BY_HREF[href];
    if (next) {
      setActiveView(next);
    }
  };

  // A failed reload after the access token's refresh token is gone keeps the stale list on screen,
  // which looks like missing reports; a re-sign-in prompt tells the user why and how to recover.
  const sessionExpired = isSessionExpiredMessage(pnlReports.error);

  return (
    <>
      <OrganizeReportsModal
        visible={nav.organizeOpen}
        assignments={nav.assignments}
        groupOptions={nav.groupOptions}
        newGroupName={nav.newGroupName}
        canAddGroup={nav.canAddGroup}
        canReset={nav.canReset}
        canEditDefault={nav.canEditDefault}
        scope={nav.scope}
        onScopeChange={nav.setScope}
        saveError={nav.saveError}
        onNewGroupNameChange={nav.setNewGroupName}
        onAddGroup={nav.addGroup}
        onMoveSection={nav.moveSection}
        onRemoveGroup={nav.removeGroup}
        onReportGroupChange={nav.setReportGroup}
        onReset={nav.resetGroups}
        onDismiss={nav.closeOrganize}
      />
      <AppLayout
        navigationWidth={WIDE_VIEWS.has(activeView) ? 265 : undefined}
        contentType={WIDE_VIEWS.has(activeView) ? 'table' : 'default'}
        maxContentWidth={WIDE_VIEWS.has(activeView) ? Number.MAX_SAFE_INTEGER : undefined}
        disableContentPaddings={FULL_BLEED_VIEWS.has(activeView)}
        toolsHide
        notifications={
          sessionExpired ? (
            <Flashbar
              items={[
                {
                  type: 'error',
                  dismissible: false,
                  header: text('sessionExpiredTitle'),
                  content: text('sessionExpiredBody'),
                  action: (
                    <Button data-testid="stargrid-session-expired-reload" onClick={() => window.location.reload()}>
                      {text('sessionExpiredReload')}
                    </Button>
                  )
                }
              ]}
              data-testid="stargrid-session-expired"
            />
          ) : undefined
        }
        navigationOpen={navigationOpen}
        onNavigationChange={({ detail }) => setNavigationOpen(detail.open)}
        navigation={
          // Three navs, so a long Reports list scrolls on its own and Admin stays in view.
          <div>
            <SideNavigation activeHref={activeHref} header={{ text: 'StarGrid', href: '#home' }} onFollow={onNavFollow} items={studioItems} />
            {nav.reportsNavGroup && (
              <ExpandableSection
                variant="default"
                headerText={text('navReportsSection')}
                expanded={nav.reportsExpanded}
                onChange={({ detail }) => nav.setReportsExpanded(detail.expanded)}
                headerActions={
                  <Button
                    variant="inline-icon"
                    iconName="edit"
                    ariaLabel={text('navOrganizeReports')}
                    onClick={nav.openOrganize}
                    data-testid="stargrid-nav-organize"
                  />
                }
              >
                <div className={styles.reports}>
                  <SideNavigation
                    activeHref={activeHref}
                    onChange={({ detail }) => nav.onNavSectionToggled(detail)}
                    onFollow={onNavFollow}
                    items={nav.reportsNavGroup.items}
                  />
                </div>
              </ExpandableSection>
            )}
            <div className={styles.admin}>
              <SideNavigation activeHref={activeHref} onFollow={onNavFollow} items={adminItems} />
            </div>
          </div>
        }
        content={content}
      />
    </>
  );
};

/**
 * StarGrid feature shell — Cloudscape AppLayout with a collapsible side navigation:
 * StarGrid Home, one "Reports" row holding the created reports in collapsible groups (hidden when
 * there are none, grouped by template and rearrangeable per user), and an "Admin" section for report
 * configuration. Shared server state lives in StarGridDataProvider; each sub-view owns its own data
 * hooks.
 */
export const StarGridPage: React.FC = () =>
  // Alpha only: in beta/prod nothing StarGrid renders or calls its API, even if the route is reached.
  IS_NON_PROD ? (
    <StarGridDataProvider>
      <StarGridShell />
    </StarGridDataProvider>
  ) : (
    <Navigate to="/" replace />
  );

export default StarGridPage;
